import type { ValueSurveyBehaviorQuery } from '@aurora/shared-generated/types/graphql-types';
import Cookies from 'universal-cookie';

const cookies = new Cookies();

/**
 * Cookie format: <timestamp|<surveyState>
 */
export const SURVEY_COMPLETION_COOKIE = 'ValueSurveyCompletion';

/**
 * Cookie format: <timestamp>|<visitsCount>
 */
export const SURVEY_VISITS_COUNT_COOKIE = 'ValueSurveyVisits';

export enum SurveyCompletionCookieValues {
  completed,
  declined
}

const SURVEY_COOKIE_DELIMITER = '|';

/**
 * For the purpose of increasing surveys visits counter, we'll consider that a 'new visit' happens after
 * 30 minutes of inactivity.
 */
const SURVEY_VISITS_INACTIVITY_TIME_MILISECONDS = 30 * 60 * 1000;

/**
 * Type for {@link SURVEY_COMPLETION_COOKIE} and {@link SURVEY_VISITS_COUNT_COOKIE} resulting object.
 */
type CookieState = {
  timestamp: number;
  state: number;
};

export function setSurveyCookie(cookieName: string, cookieState: CookieState) {
  const value = `${cookieState.timestamp}${SURVEY_COOKIE_DELIMITER}${cookieState.state}`;
  cookies.set(cookieName, encodeURIComponent(value));
}

function getSurveyCookie(cookieName: string): CookieState | null {
  const cookieValue = cookies.get(cookieName);
  if (!cookieValue) {
    return null;
  }

  const decodedCookie = decodeURIComponent(cookieValue);
  const parts = decodedCookie.split(SURVEY_COOKIE_DELIMITER);
  if (parts.length !== 2) {
    return null;
  }

  const timestamp = Number(parts[0]);
  const state = Number(parts[1]);

  if (Number.isNaN(timestamp) || Number.isNaN(state)) {
    return null;
  }

  return { timestamp, state };
}

/**
 * Check if the user has already participated in the survey and verify the date of the participation, to determine
 * if the user should take the survey again.
 *
 * @param surveyBehaviorData
 * @returns true if the user has participated in the survey within the expected time frame, meaning that
 * survey should not be prompted again.
 */
export function hasUserParticipatedInSurvey(surveyBehaviorData: ValueSurveyBehaviorQuery): boolean {
  const { timestamp: surveyDate, state: surveyState } =
    getSurveyCookie(SURVEY_COMPLETION_COOKIE) || {};

  if (!surveyState) {
    return false;
  }

  const currentDate = new Date();

  const daysSinceParticipation = Math.floor(
    (currentDate.getTime() - surveyDate) / (1000 * 60 * 60 * 24)
  );

  if (surveyState === SurveyCompletionCookieValues.completed.valueOf()) {
    const completedSinceDaysLimit: number =
      surveyBehaviorData.community.surveyProperties.surveyCompletedDays;
    if (daysSinceParticipation < completedSinceDaysLimit) {
      return true;
    }
  } else if (surveyState === SurveyCompletionCookieValues.declined.valueOf()) {
    const incompleteSinceDaysLimit: number =
      surveyBehaviorData.community.surveyProperties.surveyIncompleteDays;
    if (daysSinceParticipation < incompleteSinceDaysLimit) {
      return true;
    }
  }

  return false;
}

/**
 * Check if the user has visited the Community enough times to trigger the survey.
 *
 * @param surveyBehaviorData
 * @returns true if the visits limit is reached, meaning that survey should be prompted to the user.
 */
export function isSurveyVisitsReached(surveyBehaviorData: ValueSurveyBehaviorQuery): boolean {
  const { state: visitsCount } = getSurveyCookie(SURVEY_VISITS_COUNT_COOKIE) || {};
  const visitLimit = surveyBehaviorData.community.surveyProperties.surveyFrequencyVisits ?? 0;

  // if limit is 0, means visits requirement is disabled so don't check the cookie
  if (visitLimit < 1 || !visitsCount) {
    return true;
  }

  return visitsCount > visitLimit;
}

/**
 * Update the cookie 'ValueSurveyVisits' with the current time and increment the visit count, if needed.
 *
 * @param surveyBehaviorData
 */
export function updateSurveyVisitCookie(surveyBehaviorData: ValueSurveyBehaviorQuery): void {
  const visitLimit = surveyBehaviorData.community.surveyProperties.surveyFrequencyVisits ?? 0;
  // if limit is 0, means visit limit requirement is disabled so don't set the cookie
  if (visitLimit < 1) {
    return;
  }

  const { timestamp: cookieDate, state: visitsCount } =
    getSurveyCookie(SURVEY_VISITS_COUNT_COOKIE) || {};
  if (!visitsCount) {
    // this is the user first visit, set the cookie
    setSurveyCookie(SURVEY_VISITS_COUNT_COOKIE, { timestamp: Date.now(), state: 1 });
  } else {
    const currentDateTime = Date.now();
    if (cookieDate + SURVEY_VISITS_INACTIVITY_TIME_MILISECONDS > currentDateTime) {
      // the required 30 minutes of inactivity to consider this a 'new visit' have not passed, so update the cookie
      // with the current time without incrementing the visit count
      setSurveyCookie(SURVEY_VISITS_COUNT_COOKIE, { timestamp: Date.now(), state: visitsCount });
    } else {
      // new visit, increment the count
      const newVisitCount = visitsCount + 1;
      setSurveyCookie(SURVEY_VISITS_COUNT_COOKIE, { timestamp: Date.now(), state: newVisitCount });
    }
  }
}
